import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from '@amzn/awsui-components-react-v3';
import { ErrorBoundary } from 'react-error-boundary';
import { usePanelData } from '../../hooks/usePanelData';
import {
  QueryScope,
  FunnelStage,
  FunnelUnitLoss,
  FunnelUnitTotal,
  PanelFilter,
  PanelFilterControl,
  PanelFilterOption,
} from '../../interfaces/panel-data';
import { unitLabel } from '../../utils/format-unit';
import { Badge } from './Badge';
import { CountValue } from './CountValue';
import { InfoTooltip } from './InfoTooltip';
import { Legend } from './Legend';
import { StageBar } from './StageBar';
import { DrillDownRow } from './DrillDownRow';
import { TopReasonsChart } from './TopReasonsChart';
import { useWebsiteRunStatus, WebsiteRunStatus } from './useWebsiteRunStatus';
import './panel-tokens.scss';
import './FunnelPanel.scss';

/**
 * Keyed on the raw capability code, not on the display name. This is cosmetic and never read by a
 * user, and keying it on copy would change a capability's colour the moment someone edited its wording.
 */
const STAGE_COLORS: Record<string, string> = {
  SDO: 'var(--ovp-sdo)',
  SCI: 'var(--ovp-sci)',
  DTP: 'var(--ovp-dtp)',
};

/** Used when the panel's own chrome is not configured, so an unpublished profile still reads sensibly. */
const DEFAULT_TITLE = 'Funnel loss';
const DEFAULT_DESCRIPTION = 'Item drop-off across pipeline stages. Where was the loss, and why?';

/** Heading for a sub-stage whose unit is not configured — never an invented noun like "items". */
const UNSPECIFIED_UNIT = 'Unspecified unit';

/**
 * How the headline percentage is arrived at, on hover over it.
 *
 * Worth stating because the figure is not the division a reader will assume. It is one minus the product
 * of each stage's pass rate, so it does not equal total lost over total entered and the stage percentages
 * above it do not add up to it — 12% then 8% is 19.8%, not 20%, because each applies to its own survivors.
 * Someone checking the arithmetic and finding it "wrong" is the failure mode this prevents.
 *
 * The reason for the shape is given too, in one clause: the stages count different things (pages early,
 * SKUs and records later), so a single lost-over-entered ratio would mix units in its numerator and
 * double-count survivors in its denominator. A ratio taken inside one stage has neither problem, which is
 * what makes the product safe to take.
 */
const TOTAL_LOSS_EXPLANATION =
  'Share of what entered the funnel that did not survive to the end. Computed by chaining each ' +
  "stage's pass rate — 1 − (retained ÷ entering) multiplied across every stage — rather than dividing " +
  'total lost by total entering, because the stages count different things (pages early on, SKUs and ' +
  'records later) and summing them would mix units. Stage percentages therefore do not add up to this ' +
  'figure: each applies to what reached that stage, not to the original population.';

/**
 * The classification dropdown's "no filter" row. Empty value, so selecting it clears the filter. Typed
 * with a required `value` — Cloudscape's own `Option.value` is optional, and this one is read as the
 * selected value when no classification is chosen, so it must be a string rather than possibly absent.
 */
const ALL_CLASSIFICATIONS: SelectProps.Option & { value: string } = {
  label: 'All page types',
  value: '',
};

/**
 * What DONE does and does not claim, on hover over the status badge.
 *
 * Both halves are needed. It is read from one cycle, so it is not a history — a website that ran fine
 * yesterday and is stuck today reads RUNNING, and vice versa. And it is read from whether primitives ended,
 * not from how they ended, so DONE covers a cycle that failed outright. A reader who takes DONE for "all
 * good" would draw the opposite conclusion from the funnel below it, which is the confusion this prevents.
 */
const RUN_STATUS_EXPLANATION =
  "Whether the website's most recent processing cycle has finished. RUNNING means at least one primitive " +
  'in that cycle has not ended yet, so the numbers below are still moving; DONE means none are still ' +
  'running. DONE is not a verdict — a cycle whose primitives failed and stopped is DONE too, and what was ' +
  'lost is what the funnel below measures. Earlier cycles are not considered.';

interface FunnelPanelProps {
  scope: QueryScope;
  timeWindow?: string;
  /**
   * The website's domain, for the run-status badge — the primitives are keyed on the domain, while `scope`
   * carries the numeric `websiteId`. Omitted by the aggregate view, which has no single website to be running:
   * with no domain the badge is simply absent rather than reporting on an arbitrary one.
   */
  domain?: string;
}

/**
 * Renders the backend `asOf` snapshot id (`yyyy-MM-dd-HH`, UTC) for display. Falls back to the raw
 * value if the format is ever something else, so an unexpected id shows rather than breaking.
 */
const formatAsOf = (asOf: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})-(\d{2})$/.exec(asOf);
  return match ? `${match[1]}-${match[2]}-${match[3]} ${match[4]}:00 UTC` : asOf;
};

/**
 * The capability row's whole summary: a proportion, with no absolute count — see the `summaryText`
 * comment below for why a count cannot be truthful at this tier.
 *
 * An absent percentage is a malformed payload, not a zero, and is raised as such. Every other tier
 * detects that by calling `toLocaleString()` on a missing number; this tier formats nothing, so
 * without the check a broken payload would put the literal text "undefined% lost" on screen — the exact
 * class of meaningless output this panel is being changed to stop producing. The ErrorBoundary below
 * turns the throw into a contained "failed to load" state.
 */
const lossSummary = (lossPercent: number): string => {
  if (!Number.isFinite(lossPercent)) {
    throw new Error('Funnel payload carried a stage with no lossPercent');
  }
  return `${lossPercent}% lost`;
};

/**
 * The status badge. Info rather than green for RUNNING and neutral rather than green for DONE, deliberately:
 * neither is an outcome, and the panel's other badge is already the error-coloured one that is.
 */
const renderRunStatusBadge = (status: WebsiteRunStatus) => (
  <Badge variant={status === 'RUNNING' ? 'info' : 'neutral'}>
    {status === 'RUNNING' ? '⟳ RUNNING' : '■ DONE'}
    <InfoTooltip about="website status" text={RUN_STATUS_EXPLANATION} />
  </Badge>
);

const FunnelPanelBody: React.FC<FunnelPanelProps> = ({ scope, timeWindow = '30D', domain }) => {
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  // The two filter selections live here rather than being derived from the response, so a re-query
  // triggered by one does not have to wait for its own result to know what it filtered on. Time window
  // starts at the caller's value; the backend applies the same default when none is sent, and publishes
  // that default in `filters.timeWindow.defaultValue` so the two cannot drift.
  const [selectedTimeWindow, setSelectedTimeWindow] = useState<string>(timeWindow);
  // The whole option, not just its value: the classification filters on the opaque `dimensions` field,
  // so the exact `terms` to send travel with the choice. Null is "all", which sends no filter.
  const [classification, setClassification] = useState<PanelFilterOption | null>(null);

  // The classification filter is `in` over the exact terms the backend's facet returned for this
  // option — never a wildcard on the code, which the backend's filter allowlist forbids. Absent terms
  // fall back to the value itself, so a future filter whose field *is* its value still works here.
  const extraFilters: PanelFilter[] = useMemo(() => {
    if (!classification) return [];
    return [
      {
        field: 'dimensions',
        op: 'in',
        values: classification.terms?.length ? classification.terms : [classification.value],
      },
    ];
  }, [classification]);

  // COHORT aggregates across every website, so it has nothing to wait for. WEBSITE does: without a
  // websiteId the backend rejects the request rather than guessing, so there is no point sending it.
  const { data, isLoading, error } = usePanelData('FUNNEL', scope, {
    timeWindow: selectedTimeWindow,
    filters: extraFilters,
    enabled: scope.type === 'COHORT' || !!scope.websiteId || !!scope.program,
  });

  // Independent of the funnel request on purpose: the badge answers whether the website is still being
  // processed, which stays worth showing when the funnel snapshot itself fails to load. Null while it loads,
  // if it fails, or if nothing has indexed a primitive — all three render no badge rather than a guess.
  const { status: runStatus } = useWebsiteRunStatus(domain);

  const renderDrillContent = (stage: FunnelStage) => (
    <>
      <div className="ovp-drill__detail-title">{stage.displayName} — loss by sub-stage</div>
      {stage.subStages?.map((sub) => (
        <div key={sub.name} className="ovp-drill__substage">
          {/* Name, bar and counts only. Everything in here is one line tall, which is what keeps the
              bars of adjacent sub-stages aligned — they are meant to be compared by eye. */}
          <div className="ovp-drill__substage-main">
            {/* The icon shares the name's cell rather than taking a column of its own: at a fixed column it
                sat at the same x on every row, which put it most of the way to the bar from a short name
                like "Scraper" and left it reading as though it belonged to nothing. The name is clipped by
                the cell width and the icon sits outside the clipped span, so it survives the ellipsis and
                the full name still stays on hover. */}
            <div className="ovp-drill__substage-label">
              <span className="ovp-drill__substage-name" title={sub.displayName}>
                {sub.displayName}
              </span>
              {sub.description && <InfoTooltip about={sub.displayName} text={sub.description} />}
            </div>
            <div className="ovp-drill__substage-bar">
              <StageBar
                lostPercent={sub.lossPercent}
                retainedColor={STAGE_COLORS[stage.name] || 'var(--ovp-blue)'}
                tier="substage"
              />
            </div>
            {/* Counts are meaningful here and only here: one sub-stage counts one thing, so the unit is
                unambiguous. The unit is stated even at zero, so the copy reads the same either way. Each
                count is compact with the exact figure a hover away — see CountValue. */}
            <div className="ovp-drill__substage-nums">
              <CountValue count={sub.itemsIn} text={sub} /> in ·{' '}
              {sub.lost > 0 ? (
                <span className="ovp-up">
                  <CountValue count={sub.lost} text={sub} /> lost ({sub.lossPercent}%)
                </span>
              ) : (
                <span className="ovp-flat">
                  <CountValue count={0} text={sub} /> lost ({sub.lossPercent}%)
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
      {stage.topReasons && stage.topReasons.length > 0 && (
        <TopReasonsChart
          title={`Top drop reasons — ${stage.displayName}`}
          reasons={stage.topReasons}
          // The authoritative total for "what share of the loss is this reason". Passed rather than summed
          // from the reasons themselves because only the five largest per capability are emitted; see
          // `shareOfUnitLoss`. Null where the capability spans units, and the share is then omitted.
          unitLoss={stage.unitLoss}
        />
      )}
    </>
  );

  /**
   * One sentence per unit: what was lost, where, and why.
   *
   * There is deliberately no ordering applied and no "top N" cut. Both would have to rank pages
   * against SKUs against entities, which is the comparison this panel exists to stop making. The
   * backend emits these in pipeline order and they are rendered in that order, all of them.
   */
  const renderWorstByUnit = (worstByUnit: FunnelUnitLoss[]) => {
    if (worstByUnit.length === 0) {
      return <span className="ovp-funnel__worst-none">No loss to report at any stage.</span>;
    }

    return worstByUnit.map((worst) => (
      <div key={worst.subStage} className="ovp-funnel__worst-entry">
        <span className="ovp-funnel__worst-unit">{unitLabel(worst) || UNSPECIFIED_UNIT}</span>{' '}
        <CountValue count={worst.lost} text={worst} emphasis /> lost ({worst.lossPercent}%) at{' '}
        {worst.capabilityDisplayName} → {worst.subStageDisplayName}
        {/* No reason recorded is a real state — omit the clause rather than trailing an empty "—". */}
        {worst.topReasonDisplayName && <> — mostly {worst.topReasonDisplayName}</>}
      </div>
    ));
  };

  /**
   * The badge: the whole-funnel loss ratio, then the total lost in each unit.
   *
   * The ratio leads because it is the one figure true across every unit. The per-unit totals follow it
   * rather than being merged into a single "items" number, which is what the mockup showed and what
   * summed pages, SKUs and entities into a quantity that names nothing. `lostByUnit` is guarded because
   * it was added after the first payloads shipped: a cached response without it renders the ratio alone
   * rather than throwing, whereas a missing `totalLossPercent` is a real defect and surfaces as one.
   *
   * The ratio carries `TOTAL_LOSS_EXPLANATION`; the per-unit counts deliberately do not, since those are
   * plain sums and the explanation is only about how the percentage is arrived at.
   *
   * On the panel's own `InfoTooltip` rather than a `title`, because this string is load-bearing: it is the
   * answer to "why don't the stage percentages add up to this number?", which is the first thing a reader
   * checking the arithmetic will ask. A `title` on a non-focusable `span` is unreachable by keyboard,
   * absent on touch, and announced inconsistently by screen readers — so the reader most likely to distrust
   * the figure is the one least able to get the explanation. Cloudscape's `Popover` was here first and did
   * clear that bar, but its trigger brought a wrapper and link colouring that broke the badge's single
   * line, and it left the panel with two kinds of hover that looked and behaved differently.
   */
  const renderBadge = (totalLossPercent: number, lostByUnit: FunnelUnitTotal[] | undefined) => (
    <Badge variant="error">
      ⊘ {totalLossPercent}% total loss
      <InfoTooltip about="total loss" text={TOTAL_LOSS_EXPLANATION} />
      {(lostByUnit ?? []).map((unit) => (
        <React.Fragment key={unit.unit ?? 'unitless'}>
          {' · '}
          <CountValue count={unit.lost} text={unit} />
        </React.Fragment>
      ))}
    </Badge>
  );

  /**
   * Title, badge and snapshot on one line. Wraps rather than letting the badge push the timestamp out of
   * the panel, which `SpaceBetween` on its own does not do.
   */
  const headerContent = (
    <span className="ovp-funnel__title">
      <span>{data?.panel?.title || DEFAULT_TITLE}</span>
      {/* First of the two badges, and above the funnel rather than inside it, because it qualifies
          everything below it the way the snapshot timestamp does: a cycle still running is a funnel whose
          counts are not final yet. Also the only part of this header that survives a failed funnel request. */}
      {runStatus !== null && renderRunStatusBadge(runStatus)}
      {data && renderBadge(data.summary.totalLossPercent, data.summary.lostByUnit)}
      {data?.asOf && <span className="ovp-funnel__as-of">Snapshot {formatAsOf(data.asOf)}</span>}
    </span>
  );

  /**
   * A filter dropdown, rendered only when the backend offered values for it.
   *
   * An empty control is worse than no control: it invites a click that reveals nothing and implies the
   * data is filterable in a way it is not. `extraOption` is the classification's "All" row, which is a
   * real choice (clear the filter) rather than a value, so it does not count towards "has options".
   */
  const renderFilter = (
    label: string,
    control: PanelFilterControl | undefined,
    selectedValue: string,
    onChange: (value: string) => void,
    extraOption?: SelectProps.Option
  ) => {
    if (!control || control.options.length === 0) return null;
    const options: SelectProps.Option[] = [
      ...(extraOption ? [extraOption] : []),
      ...control.options.map((option) => ({ label: option.displayName, value: option.value })),
    ];
    return (
      <label className="ovp-funnel__filter">
        <span className="ovp-funnel__filter-label">{label}</span>
        <span className="ovp-funnel__filter-control">
          <Select
            selectedOption={options.find((option) => option.value === selectedValue) ?? options[0]}
            options={options}
            onChange={(event) => onChange(event.detail.selectedOption.value ?? '')}
            data-testid={`funnel-filter-${label.toLowerCase().replace(/\s+/g, '-')}`}
          />
        </span>
      </label>
    );
  };

  /**
   * The filters live in the panel header, as the controls of the panel rather than part of its content.
   *
   * They belong there for the same reason the snapshot timestamp does: both qualify every number below
   * them, so putting them above the legend inside the body made the body's first row a control strip and
   * pushed the funnel itself down. Rendered as `Header` actions, they also stay put while the body swaps
   * between its loading, error and loaded states — the selection is what caused the reload, so a control
   * that vanished mid-request would take the explanation of the spinner with it. That holds only because
   * `usePanelData` sets `keepPreviousData`: the filters are part of the query key, so without it `data`
   * would go undefined for the round trip and this whole row would unmount and remount around the very
   * select the user had just used.
   *
   * Absent when the payload carries no filters, which is the first-load and error case: `Header` renders
   * no actions area at all for a null, rather than an empty one.
   */
  const filterControls = data?.filters ? (
    <div className="ovp-funnel__filters">
      {renderFilter('Time window', data.filters.timeWindow, selectedTimeWindow, (value) =>
        setSelectedTimeWindow(value || selectedTimeWindow)
      )}
      {/* "Page Type" rather than the payload's "contentClassification": the values are page kinds
          (product detail, search), and the backend field name is an internal one. The response key is
          untouched — only the label a user reads changes. */}
      {renderFilter(
        'Page Type',
        data.filters.contentClassification,
        classification?.value ?? ALL_CLASSIFICATIONS.value,
        (value) => {
          // No match is the "All" row, whose empty value belongs to no option — that is the clear.
          const match = data.filters?.contentClassification.options.find(
            (candidate) => candidate.value === value
          );
          setClassification(match ?? null);
        },
        ALL_CLASSIFICATIONS
      )}
    </div>
  ) : null;

  return (
    <Box margin={{ bottom: 'l' }}>
      <Container
        header={
          <Header
            variant="h2"
            description={data?.panel?.description || DEFAULT_DESCRIPTION}
            actions={filterControls}
          >
            {headerContent}
          </Header>
        }
      >
        {isLoading ? (
          <SpaceBetween direction="horizontal" size="xs">
            <Spinner />
            <span>Loading funnel data...</span>
          </SpaceBetween>
        ) : error ? (
          <StatusIndicator type="error">Failed to load funnel data.</StatusIndicator>
        ) : data ? (
          <div className="ovp-funnel">
            {/* The key, and no caption under it. It said "bars scaled to items entering each stage", which
                stopped being true when the capability bar became a chain of pass rates rather than a length
                measured against a population — and a wrong caption is worse than none, since it is what a
                reader would use to interpret the length. The click hint went with it: the configured blurb
                in the header frames the panel, and what each row means is on the row. */}
            <Legend
              items={[
                { label: 'Retained (passed to next stage)', type: 'keep' },
                { label: 'Lost at this stage', type: 'lost' },
              ]}
            />

            <div className="ovp-funnel__stages">
              {data.stages.map((stage) => (
                <DrillDownRow
                  key={stage.name}
                  stageName={stage.displayName}
                  stageColor={STAGE_COLORS[stage.name] || 'var(--ovp-blue)'}
                  // Configured prose about the capability, which nothing rendered before — the tier had a
                  // description in every payload and no place to put it.
                  description={stage.description}
                  // Percentage only. A capability's `lost` adds up its sub-stages, which count different
                  // things — SDO loses pages at crawl and items after the scrape audit — so the sum names
                  // no real quantity. The ratio is the chain of its sub-stages' pass rates, which stays
                  // true regardless of what each of them was counting.
                  summaryText={lossSummary(stage.lossPercent)}
                  bar={
                    <StageBar
                      lostPercent={stage.lossPercent}
                      retainedColor={STAGE_COLORS[stage.name] || 'var(--ovp-blue)'}
                      tier="capability"
                    />
                  }
                  expanded={expandedStage === stage.name}
                  onToggle={() => setExpandedStage((prev) => (prev === stage.name ? null : stage.name))}
                >
                  {renderDrillContent(stage)}
                </DrillDownRow>
              ))}
            </div>

            <div className="ovp-funnel__summary">
              {renderBadge(data.summary.totalLossPercent, data.summary.lostByUnit)} Worst loss in each unit:
              <div className="ovp-funnel__worst">{renderWorstByUnit(data.summary.worstByUnit)}</div>
            </div>
          </div>
        ) : null}
      </Container>
    </Box>
  );
};

/** Declared outside the component so the boundary is not handed a new component type each render. */
const FunnelPanelError: React.FC = () => (
  <Box margin={{ bottom: 'l' }}>
    <Container header={<Header variant="h2">{DEFAULT_TITLE}</Header>}>
      <StatusIndicator type="error">Failed to load funnel data.</StatusIndicator>
    </Container>
  </Box>
);

/**
 * The panel renders backend numbers directly (`sub.itemsIn`, `data.summary.totalLossPercent`,
 * `summary.worstByUnit`), so a payload missing a field throws *during render*. react-query only catches
 * throws inside its `queryFn`, and `AppInitWrapper.componentDidCatch` deliberately rethrows to fail the
 * whole app — so without a boundary here, one malformed funnel payload white-screens the entire
 * dashboard. Contain it to this panel instead: the rest of the page, and the other panels landing in
 * this folder, stay usable.
 *
 * `resetKeys` holds the scope primitives rather than the `scope` object, which callers pass as a
 * fresh literal — keying on the object would reset the boundary on every parent render, throw again,
 * and loop. Keyed this way, a bad payload for one website clears when the user picks another instead
 * of leaving the panel dead until a full navigation.
 */
export const FunnelPanel: React.FC<FunnelPanelProps> = (props) => (
  <ErrorBoundary
    resetKeys={[props.scope.type, props.scope.websiteId, props.scope.program, props.timeWindow]}
    FallbackComponent={FunnelPanelError}
  >
    <FunnelPanelBody {...props} />
  </ErrorBoundary>
);
